import React, { useState, useEffect } from "react";
import { X, Shield, FileText, Check, AlertCircle, Loader2, Sparkles, Building2 } from "lucide-react";
import { showToast } from "../../../utils/toast";
import {
  getProjectsForCurrentUser,
  listSafetyProjectReportConfigs,
  createSafetyProjectReportConfig,
  updateSafetyProjectReportConfig,
  resolveOrgId,
} from "../../../api";

export default function SafetyProjectReportConfigModal({
  isOpen,
  onClose,
  initialProjectId = null,
  orgId: orgIdProp = null,
  onConfigSaved = null,
}) {
  const [loading, setLoading] = useState(false);
  const [savingProjectId, setSavingProjectId] = useState(null);
  const [projects, setProjects] = useState([]);
  const [configs, setConfigs] = useState({}); // Map: project_id -> config_obj
  const [inputCodes, setInputCodes] = useState({}); // Map: project_id -> input_value
  const [errorMap, setErrorMap] = useState({});

  const resolveTargetOrgId = (proj) => {
    const raw =
      orgIdProp ||
      proj?.org_id ||
      proj?.organization_id ||
      proj?.org ||
      proj?.organization?.id ||
      resolveOrgId?.() ||
      projects?.find((p) => p.org_id || p.organization_id || p.org)?.org_id ||
      projects?.find((p) => p.org_id || p.organization_id || p.org)?.organization_id ||
      projects?.find((p) => p.org_id || p.organization_id || p.org)?.org ||
      (() => {
        try {
          const userStr = localStorage.getItem("USER_DATA");
          const user = userStr && userStr !== "undefined" ? JSON.parse(userStr) : null;
          return user?.org ?? user?.organization_id ?? user?.org_id ?? null;
        } catch {
          return null;
        }
      })() ||
      localStorage.getItem("ORG_ID") ||
      localStorage.getItem("org_id");

    return raw ? Number(raw) : null;
  };

  useEffect(() => {
    if (isOpen) {
      fetchData();
    }
  }, [isOpen]);

  const fetchData = async () => {
    setLoading(true);
    setErrorMap({});
    try {
      const targetOrg = resolveTargetOrgId(null);
      const [projRes, confRes] = await Promise.all([
        getProjectsForCurrentUser().catch(() => ({ data: [] })),
        listSafetyProjectReportConfigs(targetOrg ? { org_id: targetOrg } : {}).catch(() => ({ data: [] })),
      ]);

      const projList = Array.isArray(projRes?.data)
        ? projRes.data
        : projRes?.data?.results || [];
      setProjects(projList);

      const confList = Array.isArray(confRes?.data)
        ? confRes.data
        : confRes?.data?.results || [];

      const confMap = {};
      const codeMap = {};
      confList.forEach((c) => {
        confMap[c.project_id] = c;
        codeMap[c.project_id] = c.project_code || "";
      });

      setConfigs(confMap);
      setInputCodes(codeMap);
    } catch (err) {
      console.error("Error fetching project report configs:", err);
      showToast("error", "Failed to load project report configurations.");
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (projectId, value) => {
    const sanitized = value.toUpperCase().replace(/\s+/g, "");
    setInputCodes((prev) => ({
      ...prev,
      [projectId]: sanitized,
    }));
    if (errorMap[projectId]) {
      setErrorMap((prev) => ({
        ...prev,
        [projectId]: null,
      }));
    }
  };

  const handleSave = async (project) => {
    const pId = project.id || project.pk;
    const rawCode = (inputCodes[pId] || "").trim().toUpperCase();

    if (!rawCode) {
      setErrorMap((prev) => ({
        ...prev,
        [pId]: "Project code is required.",
      }));
      return;
    }

    if (!/^[A-Z0-9_-]+$/.test(rawCode)) {
      setErrorMap((prev) => ({
        ...prev,
        [pId]: "Only uppercase letters, numbers, hyphens, and underscores allowed.",
      }));
      return;
    }

    setSavingProjectId(pId);
    setErrorMap((prev) => ({ ...prev, [pId]: null }));

    try {
      const existingConfig = configs[pId];
      let savedConfig = null;

      if (existingConfig && existingConfig.id) {
        const res = await updateSafetyProjectReportConfig(existingConfig.id, {
          project_code: rawCode,
        });
        savedConfig = res?.data;
      } else {
        const targetOrgId = resolveTargetOrgId(project);
        const payload = {
          project_id: Number(pId),
          project_code: rawCode,
        };
        if (targetOrgId) {
          payload.org_id = targetOrgId;
        }

        const res = await createSafetyProjectReportConfig(payload);
        savedConfig = res?.data;
      }

      setConfigs((prev) => ({
        ...prev,
        [pId]: savedConfig,
      }));
      setInputCodes((prev) => ({
        ...prev,
        [pId]: savedConfig.project_code,
      }));

      showToast("success", `Project code "${rawCode}" saved for ${project.name || "project"}.`);
      if (onConfigSaved) {
        onConfigSaved(savedConfig);
      }
    } catch (err) {
      console.error("Failed to save project report code:", err);
      const errMsg =
        err?.response?.data?.project_code ||
        err?.response?.data?.project_id ||
        err?.response?.data?.org_id ||
        err?.response?.data?.detail ||
        err?.response?.data?.non_field_errors?.[0] ||
        "Failed to save project report code.";
      setErrorMap((prev) => ({
        ...prev,
        [pId]: Array.isArray(errMsg) ? errMsg[0] : String(errMsg),
      }));
      showToast("error", Array.isArray(errMsg) ? errMsg[0] : String(errMsg));
    } finally {
      setSavingProjectId(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
      <div
        className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-2xl flex flex-col overflow-hidden animate-in zoom-in-95 duration-200"
        style={{ maxHeight: "88vh" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-accent/5">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10 text-primary">
              <FileText className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-foreground">
                Project Inspection Report Settings
              </h2>
              <p className="text-xs text-muted-foreground">
                Configure unique project abbreviations for dynamic Inspection Report numbering
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent/15 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Informational Banner */}
        <div className="px-6 py-3 bg-primary/5 border-b border-border flex items-start gap-2.5">
          <Sparkles className="h-4 w-4 text-primary shrink-0 mt-0.5" />
          <p className="text-xs text-muted-foreground leading-relaxed">
            When shared templates use <strong>Project Based Numbering</strong>, each project automatically embeds its configured code into generated Inspection Report Numbers (e.g. <code>ADL-NEST-AC-01</code> or <code>ADL-EVOKE-AC-01</code>).
          </p>
        </div>

        {/* Content Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground gap-3">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
              <span className="text-sm font-medium">Loading project configurations…</span>
            </div>
          ) : projects.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground text-sm">
              No projects found.
            </div>
          ) : (
            <div className="space-y-3">
              {projects.map((proj) => {
                const pId = proj.id || proj.pk;
                const isHighlight = initialProjectId && String(initialProjectId) === String(pId);
                const currentCode = configs[pId]?.project_code || "";
                const inputValue = inputCodes[pId] ?? "";
                const isDirty = inputValue !== currentCode;
                const isSaving = savingProjectId === pId;
                const error = errorMap[pId];

                return (
                  <div
                    key={pId}
                    className={`flex flex-col md:flex-row md:items-center justify-between gap-3 p-3.5 rounded-lg border transition-all ${
                      isHighlight
                        ? "bg-primary/10 border-primary/40 ring-1 ring-primary/30"
                        : "bg-background border-border hover:border-border/80"
                    }`}
                  >
                    <div className="flex items-center gap-2.5 min-w-[200px]">
                      <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-foreground">
                            {proj.name || proj.project_name || `Project #${pId}`}
                          </span>
                          {isHighlight && (
                            <span className="text-[10px] uppercase font-bold bg-primary text-primary-foreground px-1.5 py-0.5 rounded">
                              Current
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground font-mono">
                          ID: #{pId}
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-col items-start md:items-end gap-1 flex-1 max-w-xs ml-auto">
                      <div className="flex items-center gap-2 w-full">
                        <input
                          type="text"
                          maxLength={30}
                          placeholder="e.g. NEST"
                          value={inputValue}
                          onChange={(e) => handleCodeChange(pId, e.target.value)}
                          className="flex-1 px-3 py-1.5 text-xs font-mono font-bold tracking-wider rounded-md border border-border bg-card text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary uppercase"
                        />
                        <button
                          type="button"
                          disabled={isSaving || !inputValue.trim() || (!isDirty && !!currentCode)}
                          onClick={() => handleSave(proj)}
                          className={`px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1.5 shadow-sm transition-all ${
                            isDirty || !currentCode
                              ? "bg-primary text-primary-foreground hover:brightness-105"
                              : "bg-accent/15 text-muted-foreground cursor-default"
                          }`}
                        >
                          {isSaving ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : currentCode && !isDirty ? (
                            <>
                              <Check className="h-3.5 w-3.5 text-emerald-500" />
                              Saved
                            </>
                          ) : (
                            "Save"
                          )}
                        </button>
                      </div>
                      {error && (
                        <span className="text-[11px] text-destructive flex items-center gap-1 mt-0.5">
                          <AlertCircle className="h-3 w-3 shrink-0" />
                          {error}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end px-6 py-3.5 border-t border-border bg-accent/5">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-xs font-medium rounded-lg bg-accent/15 text-foreground hover:bg-accent/25 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
